import {useRef, useEffect, RefObject, useCallback} from 'react';
import {
  observe,
  InViewHookResponse,
  IntersectionOptions,
} from 'react-intersection-observer';
import {useIsomorphicLayoutEffect} from '../useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';

type SimpleInViewHookResponse<T> = {
  inView: InViewHookResponse['inView'];
  ref: RefObject<T>;
};

/**
 * This is a modified version of 'react-intersection-observer' `useInView` hook that does not export entry and therefore saves 1 re-rendering in all cases where it is not required at all. Check https://github.com/thebuilder/react-intersection-observer/blob/master/src/useInView.tsx
 */
export function useInViewEffect<T extends Element = Element>(
  cb: (inView: boolean, entry?: IntersectionObserverEntry) => void,
  {
    threshold,
    delay,
    trackVisibility,
    rootMargin,
    root,
    triggerOnce,
    skip,
    initialInView,
  }: IntersectionOptions = {},
): Omit<SimpleInViewHookResponse<T>, 'inView'> {
  const unobserve = useRef<Function>();
  const elemRef = useRef<T>(null);
  const entryRef = useRef<IntersectionObserverEntry | undefined>(undefined);
  const inViewRef = useRef(initialInView);

  const stopObserver = useCallback(() => {
    if (unobserve.current !== undefined) {
      unobserve.current();
      unobserve.current = undefined;
    }
  }, [unobserve]);

  const startObserver = useCallback(
    (element: T | null) => {
      stopObserver();
      // Skip creating the observer
      if (skip) return;

      if (element) {
        unobserve.current = observe(
          element,
          (inView: boolean, entry: IntersectionObserverEntry) => {
            entryRef.current = entry;
            inViewRef.current = inView;
            cb(inView, entryRef.current);

            if (entry.isIntersecting && triggerOnce) {
              // If it should only trigger once, unobserve the element after it's inView
              stopObserver();
            }
          },
          {
            root,
            rootMargin,
            threshold,
            // @ts-ignore
            trackVisibility,
            // @ts-ignore
            delay,
          },
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // If the threshold is an array, convert it to a string so it won't change between renders.
      Array.isArray(threshold) ? threshold.toString() : threshold,
      root,
      rootMargin,
      skip,
      trackVisibility,
      delay,
      cb,
      stopObserver,
      triggerOnce,
    ],
  );

  // This makes sure we re-observe the element if the reference changes. If react changes the ref, it's not guaranteed that this hook is re-rendered as well. Therefore `useLayoutEffect` might not find the update. Usually, you would use the function version of `ref` property to fix this. But this will make the code harder to use. Also, the function references are actually deprecated and should not be used any longer. This is a workaround as long as React does not provide a stable solution for a problem like this.
  const sideEffectElemRef = useRef(
    new Proxy(elemRef, {
      set(target, prop, value) {
        if (prop === 'current') {
          if (target[prop] !== value) {
            startObserver(value);
          }
        }
        return Reflect.set(target, prop, value);
      },
    }),
  );

  useIsomorphicLayoutEffect(() => {
    startObserver(elemRef.current);
    return () => stopObserver();
  }, [stopObserver, startObserver]);

  useEffect(() => {
    if (
      !unobserve.current &&
      entryRef.current &&
      !triggerOnce &&
      !skip &&
      inViewRef.current !== !!initialInView
    ) {
      inViewRef.current = !!initialInView;
      // If we don't have a ref, then reset the state (unless the hook is set to only `triggerOnce` or `skip`)
      // This ensures we correctly reflect the current state - If you aren't observing anything, then nothing is inView
      cb(Boolean(initialInView));
    }
  });

  return {
    ref: sideEffectElemRef.current,
  };
}
